import React, { useId } from 'react';

export interface MixerSvgProps {
  isRunning?: boolean | undefined;
  speed?: number | undefined; // 0 to 100+
  color?: string | undefined; // motor body color
  isMounted?: boolean | undefined; // true if mounted on tank top, false if standalone with shaft
  isMountedOnTank?: boolean | undefined; // alias for isMounted
  isOverload?: boolean | undefined;
  shaftLength?: number | undefined;
  bladeType?: 'propeller' | 'turbine' | 'anchor' | string | undefined;
  className?: string | undefined;
  style?: React.CSSProperties | undefined;
}

export const MixerSvg: React.FC<MixerSvgProps> = ({
  isRunning = false,
  speed = 100,
  color = '#1e40af',
  isMounted = false,
  isMountedOnTank = false,
  isOverload = false,
  bladeType = 'propeller',
  className,
  style,
}) => {
  const effectiveMounted = isMounted || isMountedOnTank;
  const uid = useId().replace(/:/g, '_');
  const animDuration = isRunning && speed > 0 ? Math.max(0.15, 1.2 / (speed / 100)) : 0;

  return (
    <g className={className} style={style}>
      <defs>
        {/* Motor Stator Body 3D Cylindrical Gradient */}
        <linearGradient id={`mixerMotorGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0f172a" />
          <stop offset="18%" stopColor={color} />
          <stop offset="38%" stopColor="#ffffff" stopOpacity="0.45" />
          <stop offset="55%" stopColor={color} />
          <stop offset="85%" stopColor="#0f172a" />
          <stop offset="100%" stopColor="#020617" />
        </linearGradient>

        {/* Cooling Ribs Shadow / Highlight Gradient */}
        <linearGradient id={`mixerFinGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(0,0,0,0.6)" />
          <stop offset="30%" stopColor="rgba(255,255,255,0.4)" />
          <stop offset="70%" stopColor="rgba(0,0,0,0.5)" />
        </linearGradient>

        {/* Stainless Steel Shaft 3D Metallic Gradient */}
        <linearGradient id={`mixerShaftGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="25%" stopColor="#cbd5e1" />
          <stop offset="50%" stopColor="#ffffff" />
          <stop offset="75%" stopColor="#94a3b8" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Gearbox / Flange Metallic Collar Gradient */}
        <linearGradient id={`mixerFlangeGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#cbd5e1" />
          <stop offset="45%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Terminal Box Gradient */}
        <linearGradient id={`mixerJunctionGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="50%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Impeller Blade Metallic Finish */}
        <linearGradient id={`mixerBladeGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#475569" />
          <stop offset="35%" stopColor="#cbd5e1" />
          <stop offset="65%" stopColor="#f8fafc" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>

        {/* Vibration / Operation Glow Filter */}
        <filter id={`mixerGlow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="0" stdDeviation="1.5" floodColor="#38bdf8" floodOpacity={isRunning ? '0.7' : '0'} />
        </filter>
      </defs>

      <style>{`
        @keyframes mixer-spin-${uid} {
          0% { transform: scaleX(1); }
          25% { transform: scaleX(0.2); }
          50% { transform: scaleX(-1); }
          75% { transform: scaleX(-0.2); }
          100% { transform: scaleX(1); }
        }
        @keyframes mixer-shaft-sheen-${uid} {
          0% { opacity: 0.4; transform: translateY(0); }
          50% { opacity: 0.9; transform: translateY(6px); }
          100% { opacity: 0.4; transform: translateY(0); }
        }
        .mixer-blade-anim-${uid} {
          transform-origin: 25px center;
          animation: mixer-spin-${uid} ${animDuration > 0 ? `${animDuration}s` : '0s'} infinite linear;
        }
      `}</style>

      {/* --- STANDALONE LONG SHAFT & IMPELLER (Freestanding Mode) --- */}
      {!effectiveMounted && (
        <g data-testid="mixer-standalone-shaft">
          {/* Main Central Stainless Steel Shaft */}
          <rect
            x="23.5"
            y="52"
            width="3"
            height="76"
            fill={`url(#mixerShaftGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="0.6"
          />

          {/* Shaft dynamic reflection line */}
          <line
            x1="24.8"
            y1="54"
            x2="24.8"
            y2="126"
            stroke="rgba(255, 255, 255, 0.7)"
            strokeWidth="0.8"
          />

          {/* Upper Agitator Blade (Fluid Disperser) */}
          <g
            className={isRunning ? `mixer-blade-anim-${uid}` : undefined}
            data-testid="mixer-upper-blade"
          >
            {/* Left Blade */}
            <path
              d="M 23.5 86 C 16 84 12 87 11 90 C 15 91 21 89 23.5 88 Z"
              fill={`url(#mixerBladeGrad-${uid})`}
              stroke="#0f172a"
              strokeWidth="0.6"
            />
            {/* Right Blade */}
            <path
              d="M 26.5 86 C 34 84 38 87 39 90 C 35 91 29 89 26.5 88 Z"
              fill={`url(#mixerBladeGrad-${uid})`}
              stroke="#0f172a"
              strokeWidth="0.6"
            />
            {/* Central Mounting Hub */}
            <ellipse cx="25" cy="87" rx="3.2" ry="2" fill="#334155" stroke="#0f172a" strokeWidth="0.6" />
          </g>

          {/* Lower Mixing Impeller / Turbine (Rushton / Marine Propeller) */}
          <g
            className={isRunning ? `mixer-blade-anim-${uid}` : undefined}
            data-testid="mixer-lower-blade"
          >
            {bladeType === 'anchor' ? (
              /* U-Shaped Anchor Agitator Blade */
              <path
                d="M 12 110 L 12 125 Q 25 133 38 125 L 38 110 L 35 110 L 35 123 Q 25 129 15 123 L 15 110 Z"
                fill={`url(#mixerBladeGrad-${uid})`}
                stroke="#0f172a"
                strokeWidth="0.8"
              />
            ) : bladeType === 'turbine' ? (
              /* 4-Blade Rushton Disc Turbine */
              <g>
                <ellipse cx="25" cy="124" rx="15" ry="3.5" fill="#475569" stroke="#0f172a" strokeWidth="0.7" />
                <rect x="10" y="120" width="3" height="8" fill={`url(#mixerBladeGrad-${uid})`} stroke="#0f172a" strokeWidth="0.6" />
                <rect x="23.5" y="120" width="3" height="8" fill={`url(#mixerBladeGrad-${uid})`} stroke="#0f172a" strokeWidth="0.6" />
                <rect x="37" y="120" width="3" height="8" fill={`url(#mixerBladeGrad-${uid})`} stroke="#0f172a" strokeWidth="0.6" />
              </g>
            ) : (
              /* High-Shear Marine Pitched Propeller */
              <g>
                <path
                  d="M 23.5 123 C 14 119 7 124 6 128 C 12 130 20 127 23.5 125 Z"
                  fill={`url(#mixerBladeGrad-${uid})`}
                  stroke="#0f172a"
                  strokeWidth="0.7"
                />
                <path
                  d="M 26.5 123 C 36 119 43 124 44 128 C 38 130 30 127 26.5 125 Z"
                  fill={`url(#mixerBladeGrad-${uid})`}
                  stroke="#0f172a"
                  strokeWidth="0.7"
                />
              </g>
            )}
            {/* Bottom Shaft Cap & Hub */}
            <circle cx="25" cy="124" r="3.2" fill="#1e293b" stroke="#0f172a" strokeWidth="0.6" />
            <circle cx="25" cy="124" r="1.5" fill="#cbd5e1" />
          </g>
        </g>
      )}

      {/* --- MOTOR DRIVE UNIT (Üst Dikey Motor Gövdesi) --- */}
      <g filter={isRunning ? `url(#mixerGlow-${uid})` : undefined}>
        {/* Top Eyebolt (Kaldırma Mapası) */}
        <circle cx="25" cy="5" r="3.5" fill="none" stroke="#475569" strokeWidth="1.2" />
        <rect x="23.8" y="7" width="2.4" height="3" fill="#334155" />

        {/* Top Fan Cowl / End Shield Dome */}
        <ellipse cx="25" cy="10" rx="14" ry="4" fill="#334155" stroke="#0f172a" strokeWidth="1" />
        <path d="M 11 10 Q 25 6 39 10" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.8" />

        {/* Main Cylindrical Stator Housing */}
        <rect
          x="12"
          y="10"
          width="26"
          height="32"
          rx="1"
          fill={`url(#mixerMotorGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="1.2"
        />

        {/* Cooling Ribs / Fins (Soğutma Kanatçıkları) */}
        <line x1="15" y1="12" x2="15" y2="40" stroke="#0f172a" strokeWidth="1.2" />
        <line x1="15.6" y1="12" x2="15.6" y2="40" stroke="rgba(255,255,255,0.3)" strokeWidth="0.6" />

        <line x1="19" y1="12" x2="19" y2="40" stroke="#0f172a" strokeWidth="1.2" />
        <line x1="19.6" y1="12" x2="19.6" y2="40" stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" />

        <line x1="23" y1="12" x2="23" y2="40" stroke="#0f172a" strokeWidth="1.2" />
        <line x1="23.6" y1="12" x2="23.6" y2="40" stroke="rgba(255,255,255,0.5)" strokeWidth="0.6" />

        <line x1="27" y1="12" x2="27" y2="40" stroke="#0f172a" strokeWidth="1.2" />
        <line x1="27.6" y1="12" x2="27.6" y2="40" stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" />

        <line x1="31" y1="12" x2="31" y2="40" stroke="#0f172a" strokeWidth="1.2" />
        <line x1="31.6" y1="12" x2="31.6" y2="40" stroke="rgba(255,255,255,0.3)" strokeWidth="0.6" />

        <line x1="35" y1="12" x2="35" y2="40" stroke="#0f172a" strokeWidth="1.2" />

        {/* Side Terminal Junction Box (Klemens Kutusu) */}
        <rect
          x="35"
          y="18"
          width="9"
          height="14"
          rx="1.5"
          fill={`url(#mixerJunctionGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="0.8"
        />
        {/* Terminal Box Screws & Cable Gland */}
        <circle cx="37" cy="20" r="0.6" fill="#cbd5e1" />
        <circle cx="37" cy="30" r="0.6" fill="#cbd5e1" />
        <rect x="44" y="23" width="3" height="4" rx="0.8" fill="#1e293b" stroke="#0f172a" strokeWidth="0.5" />

        {/* Gearbox / Bearing Neck Collar */}
        <rect
          x="16"
          y="42"
          width="18"
          height="6"
          fill={`url(#mixerFlangeGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="0.8"
        />

        {/* Heavy-Duty Tank Mounting Flange (Alt Montaj Flanşı) */}
        <rect
          data-testid="mixer-mounting-flange"
          x="10"
          y="47"
          width="30"
          height="6"
          rx="1.5"
          fill={`url(#mixerFlangeGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="1"
        />

        {/* Flange Mounting Bolts */}
        <circle cx="13" cy="50" r="1.1" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
        <circle cx="19" cy="50" r="1.1" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
        <circle cx="31" cy="50" r="1.1" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
        <circle cx="37" cy="50" r="1.1" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />

        {/* Rotating Seal Collar at bottom of flange */}
        <rect
          x="21"
          y="52"
          width="8"
          height="3"
          fill="#0f172a"
        />

        {/* Operation Status LED Indicator */}
        <circle
          cx="17"
          cy="15"
          r="1.8"
          fill={isOverload ? '#ef4444' : isRunning ? '#22c55e' : '#64748b'}
          stroke="#0f172a"
          strokeWidth="0.5"
        />
        {(isRunning || isOverload) && (
          <circle cx="17" cy="15" r="3.2" fill="none" stroke={isOverload ? '#f87171' : '#4ade80'} strokeWidth="0.6" opacity="0.8" />
        )}
      </g>
    </g>
  );
};
